import {CommonModule} from '@angular/common';
import {Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, signal} from '@angular/core';
import {RouterLink} from '@angular/router';
import {PlannerCalendar} from '../planner/planner-calendar/planner-calendar';
import {CurrentUserService} from '../../service/current-user';
import {DashboardViewService} from '../../service/dashboard-view';
import {Role} from '../../enum/role';
import {SelectSectorService} from '../../service/sector-selection';
import {NotificationsOverview} from '../notifications/notifications-overview/notifications-overview';
import {User} from '../../service/user';
import {Meeting} from '../../service/meeting';
import {Pop} from '../../service/pop';
import {PopStatusSummaryDto} from '../../dto/pop-status-summary-dto';
import {Pm} from '../../service/pm';
import {PmStatusSummaryDto} from '../../dto/pm-status-summary-dto';
import {Assessment} from '../../service/assessment';
import {AssessmentStatusSummaryDto} from '../../dto/assessment-status-summary-dto';
import {UserPerformanceSelector} from '../user/user-performance-selector/user-performance-selector';
import {UserPerformanceTab} from '../../enum/user-performance-tab';

@Component({
  selector: 'app-dashboard',
  imports: [CommonModule, RouterLink, PlannerCalendar, NotificationsOverview, UserPerformanceSelector],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class Dashboard {
  protected readonly UserPerformanceTab = UserPerformanceTab;
  private readonly currentUser = inject(CurrentUserService);
  private readonly sectorSelectionService = inject(SelectSectorService);
  private readonly userService = inject(User);
  private readonly meetingService = inject(Meeting);
  private readonly popService = inject(Pop);
  private readonly pmService = inject(Pm);
  private readonly assessmentService = inject(Assessment);
  private readonly dashboardViewService = inject(DashboardViewService);

  protected readonly currentUserId = this.currentUser.appUserId;
  protected readonly userDisplayName = this.currentUser.displayName;
  protected readonly userInitials = this.currentUser.initials;
  protected get dashboardView(): 'personal' | 'team' | null {
    return this.dashboardViewService.view;
  }

  protected readonly teamCount = signal<number | null>(null);
  protected readonly meetingCount = signal<number>(0);
  protected readonly popStatusSummary = signal<PopStatusSummaryDto | null>(null);
  protected readonly pmStatusSummary = signal<PmStatusSummaryDto | null>(null);
  protected readonly assessmentStatusSummary = signal<AssessmentStatusSummaryDto | null>(null);

  constructor() {
    effect(() => {
      if (this.dashboardView !== 'team') return;
      this.loadManagerDashboardData();
    });
  }

  private loadManagerDashboardData() {

    if (this.currentUser.hasAnyRole([Role.MANAGER])) {
      this.userService
        .retrieveUsersByManager('', 0, 1, ['totalUsers'])
        .subscribe({
          next: users => this.teamCount.set(users[0]?.totalUsers ?? 0),
          error: () => {
          },
        });
    } else {
      this.userService
        .retrieveVisibleUsersByUserCompanyIdAndRoles(
          undefined,
          0,
          1,
          [Role.EMPLOYEE, Role.TRAINEE],
          ['totalUsers'],
        )
        .subscribe({
          next: users => this.teamCount.set(users[0]?.totalUsers ?? 0),
          error: () => {
          },
        });
    }

    this.meetingService
      .retrieveMeetingsByMonth(new Date().getMonth() + 1, new Date().getFullYear(), ['id'])
      .subscribe({
        next: meetings => this.meetingCount.set(meetings.length),
        error: () => {
        },
      });

    this.popService.retrievePopStatusSummary().subscribe({
      next: summary => this.popStatusSummary.set(summary),
      error: () => this.popStatusSummary.set(null),
    });

    this.pmService.retrievePmStatusSummary().subscribe({
      next: summary => this.pmStatusSummary.set(summary),
      error: () => this.pmStatusSummary.set(null),
    });

    this.assessmentService.retrieveAssessmentStatusSummary().subscribe({
      next: summary => this.assessmentStatusSummary.set(summary),
      error: () => this.assessmentStatusSummary.set(null),
    });

  }

  protected readonly myDevelopmentRoute = computed(() => {
    const s = this.sectorSelectionService.getSelectedSectorId(), u = this.currentUser.appUser()?.id;
    return s && u ? `/sectoren/${s}/ontwikkeling/${u}` : null;
  });

  protected readonly meetingRoute = computed(() => {
    const s = this.sectorSelectionService.getSelectedSectorId();
    return s ? `/sectoren/${s}/afspraken` : null;
  });

  protected readonly assessmentsRoute = computed(() => {
    const s = this.sectorSelectionService.getSelectedSectorId();
    return s ? `/sectoren/${s}/assessments` : null;
  });

  protected readonly careersRoute = computed(() => {
    const s = this.sectorSelectionService.getSelectedSectorId();
    return s ? `/sectoren/${s}/carrierepaden` : null;
  });

  protected readonly teamRoute = computed(() => {
    const s = this.sectorSelectionService.getSelectedSectorId();
    return s ? `/sectoren/${s}/ontwikkeling` : null;
  });


}
